import { useEffect } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import {
  addTicketNote,
  bulkTickets,
  closeTicket,
  createTicket,
  fileTicket,
  findTicketCustomers,
  getCustomerTickets,
  getMyTicketCounts,
  getTicket,
  getTicketAgents,
  getTicketAssist,
  getTicketEntries,
  getTicketJobs,
  getTicketLookups,
  getTicketSummary,
  getTicketWhatsApp,
  listTickets,
  mergeTicket,
  reopenTicket,
  replyToTicket,
  sendTicketReplying,
  sendTicketWhatsApp,
  suggestTicketReply,
  uploadTicketAttachment,
  setTicketFollowUp,
  setTicketHelpers,
  setTicketOwner,
  setTicketPriority,
  takeOverFromAi,
  takeTicket,
  ticketKeys,
  type BulkTicketInput,
  type BulkTicketResult,
  type CreateTicketInput,
  type TicketActionResult,
  type TicketCloseReason,
  type TicketReplyAttachment,
  type TicketListParams,
} from "@/api/tickets";
import type { DictationOriginal } from "@/api/dictation";
import { describeError } from "@/lib/errors";

/**
 * Server state for the Tickets screen and the ticket panel.
 *
 * Polled, not pushed: the list every 15 s, an open conversation every 8 s,
 * the top bar's count every 30 s — and none of it while the tab is hidden
 * (TanStack pauses intervals in the background). The server computes the
 * open queue once per 5 s for everyone, so many people watching cost little.
 *
 * Every action refreshes everything ticket-shaped: lists, counts, the ticket,
 * its conversation. A 403 or 404 on a read is an answer, never retried.
 */
const noRetryOnRefusal = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
    return false;
  }
  return failureCount < 2;
};

/** AI calls cost money: a 403, 404, 429 or 503 is an answer, never retried. */
const noRetryOnAiRefusal = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError && (error.status === 429 || error.status === 503)) {
    return false;
  }
  return noRetryOnRefusal(failureCount, error);
};

export function useTicketList(params: TicketListParams, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.list(params),
    queryFn: ({ signal }) => listTickets(params, signal),
    enabled,
    retry: noRetryOnRefusal,
    staleTime: 5_000,
    refetchInterval: 15_000,
    // Switching views keeps the old rows on screen until the new ones land.
    placeholderData: keepPreviousData,
  });
}

/** My To do count for the top bar — on every page, for anyone with tickets.view. */
export function useMyTicketCounts(enabled: boolean) {
  return useQuery({
    queryKey: ticketKeys.counts(),
    queryFn: ({ signal }) => getMyTicketCounts(signal),
    enabled,
    retry: noRetryOnRefusal,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useTicket(id: number | null) {
  return useQuery({
    queryKey: ticketKeys.detail(id ?? 0),
    queryFn: ({ signal }) => getTicket(id as number, signal),
    enabled: id !== null,
    retry: noRetryOnRefusal,
    staleTime: 5_000,
    refetchInterval: 15_000,
  });
}

export function useTicketEntries(id: number | null) {
  return useQuery({
    queryKey: ticketKeys.entries(id ?? 0),
    queryFn: ({ signal }) => getTicketEntries(id as number, signal),
    enabled: id !== null,
    retry: noRetryOnRefusal,
    staleTime: 3_000,
    refetchInterval: 8_000,
  });
}

/** The customer's jobs, for tagging one — loaded when a picker opens. */
export function useTicketJobs(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ticketKeys.jobs(id),
    queryFn: ({ signal }) => getTicketJobs(id, signal),
    enabled,
    retry: noRetryOnRefusal,
    staleTime: 60_000,
  });
}

export function useCustomerTickets(customerId: number | null) {
  return useQuery({
    queryKey: ticketKeys.person(customerId ?? 0),
    queryFn: ({ signal }) => getCustomerTickets(customerId as number, signal),
    enabled: customerId !== null,
    retry: noRetryOnRefusal,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

/** Categories, sources, saved replies: change rarely. */
export function useTicketLookups() {
  return useQuery({
    queryKey: ticketKeys.lookups(),
    queryFn: ({ signal }) => getTicketLookups(signal),
    retry: noRetryOnRefusal,
    staleTime: 10 * 60_000,
  });
}

/** A phone number or customer number, typed into "New ticket". */
export function useFindTicketCustomers(search: string) {
  const usable =
    /^\+?\d[\d\s-]{0,16}$/.test(search) && search.replace(/\D/g, "").length > 0;
  return useQuery({
    queryKey: ticketKeys.customers(search),
    queryFn: ({ signal }) => findTicketCustomers(search, signal),
    enabled: usable,
    retry: noRetryOnRefusal,
    staleTime: 30_000,
  });
}

export function useTicketAgents(enabled = true) {
  return useQuery({
    queryKey: ticketKeys.agents(),
    queryFn: ({ signal }) => getTicketAgents(signal),
    enabled,
    retry: noRetryOnRefusal,
    staleTime: 60_000,
  });
}

/** The server's words for a refusal are written for people; use them. */
export function describeTicketError(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "That ticket no longer exists.";
  }
  return describeError(error);
}

/** The job-check outcomes worth a toast; the rest ("no action needed") are silent. */
const JOB_CHECK_TOASTED = new Set<string>([
  "review_opened",
  "complaint_added",
  "job_already_complained",
  "payout_already_generated",
  "job_replaced",
  "job_changed",
]);

/** After a close or filing: what the complaint job check did, in the server's words. */
function announceJobCheck(result: TicketActionResult) {
  const check = result.jobCheck;
  if (check && JOB_CHECK_TOASTED.has(check.outcome) && check.message) {
    toast.message("Job check", { description: check.message });
  }
  if (result.frequentlyMissedTagRemoved) {
    toast.message("The “frequently missed” tag was removed from this customer");
  }
}

/**
 * One action on a ticket: on success a toast (unless `quiet`) and everything
 * ticket-shaped refreshed; on failure a toast with the server's reason.
 */
function useTicketAction<TInput>(
  run: (input: TInput) => Promise<TicketActionResult>,
  success: ((input: TInput, result: TicketActionResult) => string) | null,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (result, input) => {
      if (success) toast.success(success(input, result));
      announceJobCheck(result);
    },
    onError: (error) => {
      toast.error(describeTicketError(error));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ticketKeys.all }),
  });
}

export interface TextInput {
  ticketId: number;
  text: string;
  original?: DictationOriginal | undefined;
}

/** A reply: no success toast — the message appearing in the chat says it. */
export function useReplyToTicket() {
  return useTicketAction(
    ({
      ticketId,
      text,
      original,
      attachment,
    }: TextInput & { attachment?: TicketReplyAttachment | undefined }) =>
      replyToTicket(ticketId, {
        text,
        ...(original ? { original } : {}),
        ...(attachment ? { attachment } : {}),
      }),
    null,
  );
}

/** A reply on the customer's WhatsApp, through the inbox's send path. */
export function useSendTicketWhatsApp() {
  return useTicketAction(
    ({
      ticketId,
      threadId,
      text,
      clientMessageId,
    }: {
      ticketId: number;
      threadId: number;
      text: string;
      /** One per message, kept across a retry so a lost reply is never sent twice. */
      clientMessageId: string;
    }) => sendTicketWhatsApp(ticketId, { threadId, text, clientMessageId }),
    null,
  );
}

export function useAddTicketNote() {
  return useTicketAction(
    ({
      ticketId,
      text,
      original,
      mentionAdminIds,
    }: TextInput & { mentionAdminIds?: number[] }) =>
      addTicketNote(ticketId, {
        text,
        ...(original ? { original } : {}),
        ...(mentionAdminIds?.length ? { mentionAdminIds } : {}),
      }),
    null,
  );
}

export function useSetTicketOwner() {
  return useTicketAction(
    ({ ticketId, adminId }: { ticketId: number; adminId: number; name: string }) =>
      setTicketOwner(ticketId, adminId),
    ({ name }) => `Given to ${name}`,
  );
}

export function useTakeTicket() {
  return useTicketAction(
    ({ ticketId }: { ticketId: number }) => takeTicket(ticketId),
    () => "The ticket is yours",
  );
}

export function useSetTicketHelpers() {
  return useTicketAction(
    ({ ticketId, adminIds }: { ticketId: number; adminIds: number[] }) =>
      setTicketHelpers(ticketId, adminIds),
    () => "Helpers updated",
  );
}

export function useSetTicketFollowUp() {
  return useTicketAction(
    ({ ticketId, at }: { ticketId: number; at: string | null; label?: string }) =>
      setTicketFollowUp(ticketId, at),
    ({ at, label }) => (at ? `Follow up ${label ?? "set"}` : "Follow-up cleared"),
  );
}

export function useSetTicketPriority() {
  return useTicketAction(
    ({ ticketId, important }: { ticketId: number; important: boolean }) =>
      setTicketPriority(ticketId, important),
    ({ important }) => (important ? "Marked important" : "Marked normal"),
  );
}

export function useFileTicket() {
  return useTicketAction(
    ({
      ticketId,
      ...input
    }: {
      ticketId: number;
      categoryId: number;
      subcategoryId?: number;
      jobId?: number;
    }) => fileTicket(ticketId, input),
    (_input, result) =>
      result.taggedJobId
        ? `Saved — job #${result.taggedJobId} was tagged: the job that existed when the ticket was raised`
        : "Saved",
  );
}

export function useCloseTicket() {
  return useTicketAction(
    ({
      ticketId,
      ...input
    }: {
      ticketId: number;
      jobId?: number;
      message?: string;
      original?: DictationOriginal;
      reason?: TicketCloseReason;
      note?: string;
    }) => closeTicket(ticketId, input),
    () => "Ticket closed",
  );
}

/** Closes this ticket as a duplicate of another of the same person's. */
export function useMergeTicket() {
  return useTicketAction(
    ({
      ticketId,
      intoTicketId,
    }: {
      ticketId: number;
      intoTicketId: number;
      /** For the toast: "HELP12", "HELP34". */
      numbers: { from: string; into: string };
    }) => mergeTicket(ticketId, intoTicketId),
    ({ numbers }) => `${numbers.from} closed as a duplicate of ${numbers.into}`,
  );
}

const BULK_VERB: Record<BulkTicketInput["action"], string> = {
  assign: "Given",
  follow_up: "Follow-up set on",
  priority: "Updated",
  close: "Closed",
};

/** What a skipped ticket's reason means, for the toast's parenthesis. */
function skippedHint(result: BulkTicketResult): string {
  return result.skipped.length > 0 &&
    result.skipped.every((s) => /needs its job/i.test(s.reason))
    ? " (need their job)"
    : "";
}

/**
 * Bulk assign / follow up / priority / close (manage). The toast says how many
 * went and which were skipped and why; `labels` maps a ticket id to its number.
 */
export function useBulkTickets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input }: { input: BulkTicketInput; labels: Map<number, string> }) =>
      bulkTickets(input),
    onSuccess: (result, { input, labels }) => {
      const done = result.done.length;
      const skipped = result.skipped.length;
      const title =
        `${BULK_VERB[input.action]} ${done}` +
        (skipped ? ` · ${skipped} skipped${skippedHint(result)}` : "");
      const shown = result.skipped.slice(0, 8);
      const description = skipped
        ? shown
            .map((s) => `${labels.get(s.ticketId) ?? `#${s.ticketId}`}: ${s.reason}`)
            .join("; ") +
          (skipped > shown.length ? `; and ${skipped - shown.length} more` : "")
        : undefined;
      const options = description ? { description } : {};
      if (done === 0) toast.warning(`Nothing changed · ${skipped} skipped`, options);
      else if (skipped) toast.warning(title, options);
      else toast.success(title);
    },
    onError: (error) => toast.error(describeTicketError(error)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ticketKeys.all }),
  });
}

export function useReopenTicket() {
  return useTicketAction(
    ({ ticketId }: { ticketId: number }) => reopenTicket(ticketId),
    () => "Ticket reopened",
  );
}

export function useTakeOverFromAi() {
  return useTicketAction(
    ({ ticketId }: { ticketId: number }) => takeOverFromAi(ticketId),
    () => "You have the ticket — the AI has stopped answering",
  );
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketInput) => createTicket(input),
    onSuccess: (created) => toast.success(`Ticket ${created.ticketNumber} opened`),
    onError: (error) => toast.error(describeTicketError(error)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ticketKeys.all }),
  });
}

/**
 * The customer's WhatsApp threads, for the reply box's channel switch. Only
 * asked for when it can be used (a customer ticket, `whatsapp.send`). A failure
 * is not shown: the reply box simply offers the app alone.
 */
export function useTicketWhatsApp(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ticketKeys.whatsapp(id),
    queryFn: ({ signal }) => getTicketWhatsApp(id, signal),
    enabled,
    retry: noRetryOnRefusal,
    staleTime: 30_000,
  });
}

/** Uploads a reply's photo or PDF. No toast: the box shows the chip or the error. */
export function useUploadTicketAttachment() {
  return useMutation({
    mutationFn: ({ ticketId, file }: { ticketId: number; file: File }) =>
      uploadTicketAttachment(ticketId, file),
  });
}

/** The heartbeat interval; the server keeps a beat for 15 s. */
const REPLYING_BEAT_MS = 10_000;

/**
 * While `active` (the box has focus and words), tells the server every 10 s
 * that this person is writing, so others see "Arjun is replying…". Failures are
 * ignored — it is a courtesy, not a save.
 */
export function useReplyingBeat(
  ticketId: number,
  mode: "reply" | "note",
  active: boolean,
) {
  useEffect(() => {
    if (!active) return;
    const beat = () => void sendTicketReplying(ticketId, mode).catch(() => undefined);
    beat();
    const timer = setInterval(beat, REPLYING_BEAT_MS);
    return () => clearInterval(timer);
  }, [ticketId, mode, active]);
}

/** Whether AI help applies to this ticket: a draft on opening, a summary strip. */
export function useTicketAssist(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ticketKeys.assist(id),
    queryFn: ({ signal }) => getTicketAssist(id, signal),
    enabled,
    retry: noRetryOnAiRefusal,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** An English reply written from the conversation. Never sent by itself. */
export function useSuggestReply() {
  return useMutation({
    mutationFn: ({ ticketId }: { ticketId: number }) => suggestTicketReply(ticketId),
  });
}

/** One or two sentences on a long conversation; fetched when the strip shows. */
export function useTicketSummary(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ticketKeys.summary(id),
    queryFn: ({ signal }) => getTicketSummary(id, signal),
    enabled,
    retry: noRetryOnAiRefusal,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
